// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseCheckbox } from '~/components/common';
// Others
import { PermissionRoleEnum } from '~/utils/enum/common';
import { defaultValuesPermissionForm, schema } from './helper';
import { IFormRole, IPermissionRole, IPermissionRoleItem } from '~/utils/interface/role';
// Styles, images, icons
import styles from './PermissionRole.module.scss';
import './PermissionRole.scss';
import { icons } from '~/assets';

type Props = {
  initialCodes?: string[];
};

const cx = classNames.bind(styles);

export const PERMISSION_FIELDS_BASE: (keyof IPermissionRoleItem)[] = ['view', 'detail', 'create', 'edit', 'delete'];

export const PERMISSION_FIELDS: (keyof IPermissionRoleItem)[] = [...PERMISSION_FIELDS_BASE, 'viewAll'];

export const PERMISSION_TYPES_ROLE = Object.values(PermissionRoleEnum);

export const PERMISSION_TYPES_HAVE_IS_ALL: PermissionRoleEnum[] = [PermissionRoleEnum.JOB, PermissionRoleEnum.TASK];

export const getPermissionFieldsForType = (type: PermissionRoleEnum): (keyof IPermissionRoleItem)[] =>
  PERMISSION_TYPES_HAVE_IS_ALL.includes(type) ? [...PERMISSION_FIELDS_BASE, 'viewAll'] : [...PERMISSION_FIELDS_BASE];

const PermissionRole = (props: Props) => {
  //#region Destructuring Props
  const { initialCodes } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/role', 'common/base']);

  const { setValue: setValueRole } = useFormContext<IFormRole>();

  const { control, setValue, reset } = useForm<IPermissionRole>({
    resolver: yupResolver(schema()),
    defaultValues: defaultValuesPermissionForm,
  });

  const watchPermission = useWatch<IPermissionRole>({ control });
  //#endregion Declare Hook

  //#region Selector

  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  const permissionFormValues = useMemo<Partial<IPermissionRole>>(
    () => watchPermission || ({} as Partial<IPermissionRole>),
    [watchPermission]
  );
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!initialCodes) return;

    const initialPermission = convertDetailRoleToForm(initialCodes);

    reset(initialPermission);
  }, [initialCodes]);

  useEffect(() => {
    const codes = convertDataCodesFormRole(permissionFormValues);
    setValueRole('codes', codes, { shouldDirty: true });
  }, [permissionFormValues, setValueRole]);
  //#endregion Implement Hook

  //#region Handle Function
  const convertDetailRoleToForm = (codes: string[]): IPermissionRole => {
    const result: IPermissionRole = {};

    PERMISSION_TYPES_ROLE.forEach((type) => {
      result[type] = {};
      getPermissionFieldsForType(type).forEach((field) => {
        result[type][field as keyof IPermissionRoleItem] = false;
      });
    });

    codes.forEach((code) => {
      if (code === '*:*') {
        PERMISSION_TYPES_ROLE.forEach((type) => {
          getPermissionFieldsForType(type).forEach((field) => {
            result[type][field as keyof IPermissionRoleItem] = true;
          });
        });
        return;
      }

      const [entity, action] = code.split(':');

      if (entity && action && result[entity]) {
        const fieldsForEntity = getPermissionFieldsForType(entity as PermissionRoleEnum);
        if (action === '*') {
          fieldsForEntity.forEach((field) => {
            result[entity][field as keyof IPermissionRoleItem] = true;
          });
        } else if (fieldsForEntity.includes(action as keyof IPermissionRoleItem)) {
          result[entity][action as keyof IPermissionRoleItem] = true;
        }
      }
    });

    return result;
  };

  const convertDataCodesFormRole = (permissions: Partial<IPermissionRole>): string[] => {
    const codes: string[] = [];

    const viewAllSelected =
      PERMISSION_TYPES_ROLE.length > 0 &&
      PERMISSION_TYPES_ROLE.every((type) => {
        const typePermissions = permissions[type];
        if (!typePermissions) return false;
        const fields = getPermissionFieldsForType(type);
        return fields.every((field) => typePermissions[field] === true);
      });

    if (viewAllSelected) {
      return ['*:*'];
    }

    PERMISSION_TYPES_ROLE.forEach((type) => {
      const typePermissions = permissions[type];
      if (!typePermissions) return;

      const fields = getPermissionFieldsForType(type);
      const isTypeAllSelected = fields.every((field) => typePermissions[field] === true);

      if (isTypeAllSelected) {
        codes.push(`${type}:*`);
      } else {
        fields.forEach((field) => {
          if (typePermissions[field] === true) {
            codes.push(`${type}:${field}`);
          }
        });
      }
    });

    return codes;
  };

  const handleCheckAllPermission = (type: PermissionRoleEnum, checked: boolean) => {
    getPermissionFieldsForType(type).forEach((field) => {
      setValue(`${type}.${field}`, checked);
    });
  };

  const handleButtonAllPermission = (checked: boolean) => {
    PERMISSION_TYPES_ROLE.forEach((type) => {
      handleCheckAllPermission(type, checked);
    });
  };

  const isTypeAllSelected = (type: PermissionRoleEnum) => {
    const typePermissions = permissionFormValues?.[type];
    if (!typePermissions) return false;

    const fields = getPermissionFieldsForType(type);
    return fields.every((field) => !!typePermissions[field]);
  };

  const isTypePartiallySelected = (type: PermissionRoleEnum) => {
    const typePermissions = permissionFormValues?.[type];
    if (!typePermissions) return false;

    const fields = getPermissionFieldsForType(type);
    return fields.some((field) => !!typePermissions[field]);
  };

  const viewAllSelected =
    PERMISSION_TYPES_ROLE.length > 0 && PERMISSION_TYPES_ROLE.every((type) => isTypeAllSelected(type));
  const viewAllIndeterminate = PERMISSION_TYPES_ROLE.some((type) => isTypePartiallySelected(type)) && !viewAllSelected;

  const renderPermission = (type: PermissionRoleEnum, label: string) => {
    const isCheckAll = isTypeAllSelected(type);
    const isIndeterminate = !isCheckAll && isTypePartiallySelected(type);
    const haveIsAll = PERMISSION_TYPES_HAVE_IS_ALL.includes(type);

    const handleToggleIsAll = (checked: boolean) => {
      setValue(`${type}.viewAll`, checked);
    };

    return (
      <Grid2 size={4}>
        <Accordion defaultExpanded>
          <AccordionSummary
            className={cx('summaryButton')}
            expandIcon={<img src={icons.commonIconArrowBottom} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <BaseCheckbox
                label={label}
                checked={isCheckAll}
                indeterminate={isIndeterminate}
                onChange={(value) => handleCheckAllPermission(type, value)}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails className={cx('detailsRole')}>
            <Stack spacing={3} className={cx('detailsRoleList')}>
              <Controller
                control={control}
                name={`${type}.view`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseCheckbox
                      label={t('common_button_view', { ns: 'common/base' })}
                      checked={value || false}
                      onChange={(value) => onChange(value)}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name={`${type}.detail`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseCheckbox
                      label={t('common_button_detail', { ns: 'common/base' })}
                      checked={value || false}
                      onChange={(value) => onChange(value)}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name={`${type}.create`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseCheckbox
                      label={t('common_button_create', { ns: 'common/base' })}
                      checked={value || false}
                      onChange={(value) => onChange(value)}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name={`${type}.edit`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseCheckbox
                      label={t('common_button_edit', { ns: 'common/base' })}
                      checked={value || false}
                      onChange={(value) => onChange(value)}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name={`${type}.delete`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseCheckbox
                      label={t('common_button_delete', { ns: 'common/base' })}
                      checked={value || false}
                      onChange={(value) => onChange(value)}
                    />
                  );
                }}
              />

              {haveIsAll && (
                <Controller
                  control={control}
                  name={`${type}.viewAll`}
                  render={({ field: { onChange, value } }) => (
                    <BaseCheckbox
                      label={t('common_button_view_all_data', { ns: 'common/base' })}
                      checked={value || false}
                      onChange={(checked) => {
                        onChange(checked);
                        handleToggleIsAll(checked);
                      }}
                    />
                  )}
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Grid2>
    );
  };
  //#endregion Handle Function

  return (
    <Box id='permissionRoleComponent' className={cx('container')}>
      <Stack direction={'row'} gap={3} alignItems={'center'}>
        <BaseCheckbox
          checked={viewAllSelected}
          indeterminate={viewAllIndeterminate}
          onChange={(value) => handleButtonAllPermission(value)}
        />

        <Typography className={cx('header')}>{t('role_form_title_permissions')}</Typography>
      </Stack>

      <Grid2 container rowSpacing={5} columnSpacing={5}>
        {renderPermission(PermissionRoleEnum.JOB, t('role_form_title_job'))}

        {renderPermission(PermissionRoleEnum.LEAD, t('role_form_title_lead'))}

        {renderPermission(PermissionRoleEnum.CLIENT, t('role_form_title_client'))}

        {renderPermission(PermissionRoleEnum.TASK, t('role_form_title_task'))}

        {renderPermission(PermissionRoleEnum.SCHEDULE, t('role_form_title_schedule'))}

        {renderPermission(PermissionRoleEnum.QUOTE, t('role_form_title_quote'))}

        {renderPermission(PermissionRoleEnum.ORDER, t('role_form_title_order'))}

        {renderPermission(PermissionRoleEnum.INVOICE, t('role_form_title_invoice'))}

        {renderPermission(PermissionRoleEnum.PRODUCT, t('role_form_title_product'))}

        {renderPermission(PermissionRoleEnum.PACKAGE, t('role_form_title_package'))}

        {renderPermission(PermissionRoleEnum.QUARANTINE, t('role_form_title_quarantine'))}

        {renderPermission(PermissionRoleEnum.REPORT, t('role_form_title_report'))}

        {renderPermission(PermissionRoleEnum.TRUCK, t('role_form_title_truck'))}

        {renderPermission(PermissionRoleEnum.TRAILER, t('role_form_title_trailer'))}

        {renderPermission(PermissionRoleEnum.EQUIPMENT, t('role_form_title_equipment'))}

        {renderPermission(PermissionRoleEnum.VENDOR, t('role_form_title_vendor'))}

        {renderPermission(PermissionRoleEnum.PURCHASE, t('role_form_title_purchase'))}

        {renderPermission(PermissionRoleEnum.USER, t('role_form_title_user'))}

        {renderPermission(PermissionRoleEnum.ROLE, t('role_form_title_role'))}

        {renderPermission(PermissionRoleEnum.COUPON, t('role_form_title_coupon'))}

        {renderPermission(PermissionRoleEnum.EVENT_TYPE, t('role_form_title_eventType'))}

        {renderPermission(PermissionRoleEnum.CATEGORY, t('role_form_title_category'))}

        {renderPermission(PermissionRoleEnum.CHECKLIST, t('role_form_title_checklist'))}
      </Grid2>
    </Box>
  );
};

export default PermissionRole;
